export const colors = {
  primary: '#00AEEF', // brand accent (PO)
  primaryHover: '#33C1F3',
  primaryActive: '#0095CC',
  success: '#3FB950', // wireframe
  warning: '#D29922', // wireframe
  error: '#F85149', // wireframe
  info: '#58A6FF', // wireframe

  // === Backgrounds (PO values - warmer) ===
  bgBase: '#1E1F22', // app background (PO)
  bgElevated: '#2A2C31', // cards, panels (PO)
  bgSunken: '#161B22', // table header / deepest
  bgControl: '#21262D', // inputs/selects

  // === Surface hierarchy (wireframe) ===
  surfaceSecondary: '#1C2128',
  surfaceTertiary: '#21262D',

  // === Interactive states ===
  border: '#3A3D42', // dividers (PO)
  hover: '#2F3238', // row/btn hover
  selected: '#363A40', // selected row/cell

  // === Text (wireframe - proven WCAG contrast) ===
  text: '#E6EDF3',
  textSec: '#8B949E',
  textTertiary: '#6E7681',
  textDisabled: '#6E7681',

  // === AI-specific (wireframe) ===
  aiAccent: '#A371F7',
  aiGlow: 'rgba(163, 113, 247, 0.4)',
  accentGradient: 'linear-gradient(135deg, #00AEEF 0%, #A371F7 100%)',
  findingBg: 'rgba(0, 174, 239, 0.08)',
};

export const BORDER_RADIUS = {
  borderRadius: 8,
  borderRadiusLG: 12,
  borderRadiusSM: 6, // updated from 4px
  borderRadiusXS: 4,
};

// === Line Heights (NEW) ===
export const LINE_HEIGHT = {
  tight: 1.25,
  normal: 1.5,
  relaxed: 1.75,
};

export const TYPOGRAPHY_SIZES = {
  H1: '2rem', // 32px
  H2: '1.75rem', // 28px
  H3: '1.625rem', // 26px
  H4: '1.5rem', // 24px
  H5: '1.375rem', // 22px
  H6: '1.25rem', // 20px - xl
  H7: '1.125rem', // 18px - lg
  BIG: '1rem', // 16px - md
  NORMAL: '0.875rem', // 14px - base
  SMALL: '0.75rem', // 12px - sm
  XS: '0.6875rem', // 11px - xs
};

// CSS variable names for use in CSS-in-JS
export const FONT_SIZE_VARS = {
  xs: 'var(--font-size-xs)',
  sm: 'var(--font-size-sm)',
  base: 'var(--font-size-base)',
  md: 'var(--font-size-md)',
  lg: 'var(--font-size-lg)',
  xl: 'var(--font-size-xl)',
} as const;

export const TYPOGRAPHY = {
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  fontFamilyMono: '"SF Mono", Monaco, "Cascadia Code", monospace',
  fontSize: 14,
  fontSizeLG: 16,
  fontSizeSM: 13,
  fontSizeXL: 20,
  fontSizeHeading1: 32,
  fontSizeHeading2: 24,
  fontSizeHeading3: 20,
  fontSizeHeading4: 16,
  fontSizeHeading5: 14,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightSemiBold: 600,
};

export const SPACING = {
  padding: 16,
  paddingLG: 24,
  paddingSM: 12,
  paddingXS: 8,
  paddingXL: 32,
  paddingXXL: 84,
};

export const COMPONENT_SIZE = {
  controlHeight: 44, // minimum 44px for accessibility
  controlHeightLG: 48,
  controlHeightSM: 36,
  controlHeightXS: 28,
  tableRowHeight: 64, // more breathing room
};

export const SHADOWS = {
  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  boxShadowSecondary:
    '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  boxShadowTertiary:
    '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
};

// === Z-Index Scale (expanded) ===
export const Z_INDEX = {
  zIndexBase: 0,
  zIndexDropdown: 1000,
  zIndexSticky: 1020,
  zIndexModalBackdrop: 1040,
  zIndexModal: 1050,
  zIndexTooltip: 1060,
  zIndexNotification: 1070,
  // Legacy alias for compatibility
  zIndexPopupBase: 1000,
};

// === Responsive Breakpoints (NEW) ===
export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
};

export const OPACITY = {
  opacityLoading: 0.65,
};

// === Motion (updated fast duration) ===
export const MOTION = {
  motionDurationFast: '0.15s', // updated from 0.1s
  motionDurationMid: '0.2s',
  motionDurationSlow: '0.3s',
};

// === Layout dimensions ===
export const LAYOUT = {
  sidebarWidth: 72,
  subSidebarWidth: 240,
  headerHeight: 48,
  touchTarget: 44,
  iconTarget: 36,
};

// === Viewer annotation dimensions ===
export const ANNOTATION_STYLE = {
  lineWidthActive: 3,
  lineWidthInactive: 2,
  // Round handles — Doppler traces and the shared annotation style. Linear
  // endpoints use the crosshair tokens below (AP-810), not these.
  handleRadiusActive: 6,
  handleRadiusInactive: 4,
  // Linear endpoint crosshairs (AP-810) — arm half-length and stroke, canvas px.
  // Arms run past the ~5px grab radius; the grab point is the intersection.
  crosshairArmActive: 7,
  crosshairArmInactive: 6,
  crosshairWidthActive: 2,
  crosshairWidthInactive: 1.5,
};
